import { BUILDING_LABELS } from '@bienenkampf/shared';
import { Button, Progress } from '@bienenkampf/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { formatAbsRel, formatDuration } from '../lib/time.js';
import { useTRPC } from '../lib/trpc.js';
import type { HiveView } from './types.js';

export function BuildQueue({ view, now }: { view: HiveView; now: Date }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const key = trpc.hive.get.queryKey({ hiveId: view.id });
  const cancel = useMutation(
    trpc.hive.build.cancel.mutationOptions({
      onSettled: () => qc.invalidateQueries({ queryKey: key }),
    }),
  );

  // Countdown abgelaufen -> nachladen (der Worker hat i. d. R. schon fertiggestellt; sonst kommt das WS-Event).
  const head = view.queue[0];
  const overdue = head?.finishesAt ? head.finishesAt.getTime() <= now.getTime() : false;
  useEffect(() => {
    if (overdue) {
      const t = window.setTimeout(() => void qc.invalidateQueries({ queryKey: key }), 1500);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [overdue, qc, key]);

  if (view.queue.length === 0) return <p className="text-sm text-stone-400">Nichts in Arbeit.</p>;

  return (
    <ul className="flex flex-col gap-3">
      {view.queue.map((q) => {
        const running = q.startedAt && q.finishesAt;
        const total = running ? q.finishesAt!.getTime() - q.startedAt!.getTime() : 0;
        const done = running ? Math.min(1, (now.getTime() - q.startedAt!.getTime()) / total) : 0;
        const remaining = running ? (q.finishesAt!.getTime() - now.getTime()) / 1000 : null;
        return (
          <li key={q.id} className="rounded-lg bg-stone-900 p-3">
            <div className="flex items-center justify-between gap-2">
              <div>
                <div className="font-medium">
                  {BUILDING_LABELS[q.type]} → Stufe {q.targetLevel}
                </div>
                <div className="text-xs text-stone-400">
                  {running ? (
                    remaining! > 0 ? (
                      <>fertig {formatAbsRel(q.finishesAt!, now)}</>
                    ) : (
                      'wird abgeschlossen…'
                    )
                  ) : (
                    'wartet'
                  )}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => cancel.mutate({ hiveId: view.id, queueId: q.id })}
                disabled={cancel.isPending || (remaining !== null && remaining <= 0)}
                title="Abbrechen (volle Rückerstattung)"
              >
                ✕
              </Button>
            </div>
            {running && <Progress value={done} className="mt-2" />}
            {running && remaining! > 0 && (
              <div className="mt-1 text-right text-xs tabular-nums text-stone-500">
                {formatDuration(remaining!)}
              </div>
            )}
          </li>
        );
      })}
      {cancel.error && <li className="text-sm text-red-400">{cancel.error.message}</li>}
    </ul>
  );
}
